import { useState, useEffect } from 'react';
import type { SimulatorQuoteData } from '../page';

const GRAVE_OPTIONS = [
  { value: 100000, label: '1기 (기본 벌초)' },
  { value: 200000, label: '2기' },
  { value: 300000, label: '4기 (문중 묶음 할인 적용)' },
  { value: 500000, label: '6기 이상 (별도 문의)' },
];

const GARDEN_OPTIONS = [
  { value: 150000, label: '소형 정원 (50평 이하)' },
  { value: 300000, label: '중형 정원 (100평 이하)' },
  { value: 500000, label: '대형 정원 (200평 이하)' },
  { value: 800000, label: '특대형 정원 (200평 초과)' },
];

const GRAVE_CORE_FACTORS = [
  { id: 'grass', price: 20000, title: '풀 높이 (허리 이상)', desc: '우거진 잡풀로 인한 작업 시간 증가' },
  { id: 'tree', price: 50000, title: '잡목/가시나무 제거', desc: '엔진톱 및 특수 장비 투입 필요' },
  { id: 'slope', price: 30000, title: '급경사 지형', desc: '경사로 인한 안전 작업 할증 적용' },
  { id: 'access', price: 20000, title: '도보 진입 (300m 이상)', desc: '차량 진입 불가로 인한 장비 운반비' },
];

const GARDEN_CORE_FACTORS = [
  { id: 'lawn', price: 80000, title: '잔디 관리 (깎기/시비)', desc: '전문 장비를 이용한 잔디 정리 및 영양 공급' },
  { id: 'pruning', price: 100000, title: '수목 전정 (5그루 이상)', desc: '나무 형태 정리 및 가지치기 작업' },
  { id: 'flowerbed', price: 60000, title: '화단 관리 (잡초 제거)', desc: '화단 내 잡초 제거 및 토양 정비' },
  { id: 'hedge', price: 70000, title: '생울타리 정리', desc: '울타리 형태 정리 및 높이 조절' },
];

const GRAVE_EXTRA_FACTORS = [
  { id: 'stone', price: 30000, title: '석물 세척', desc: '비석·상석 고압 세척 서비스' },
  { id: 'weed', price: 15000, title: '잡초 재생 방지', desc: '밑동 재생 방지 처리' },
  { id: 'drone', price: 20000, title: '드론 촬영 리포트', desc: '전/후 드론 촬영 및 보고서 발송' },
  { id: 'night', price: 25000, title: '야간·주말 작업', desc: '평일 외 작업 할증' },
  { id: 'rain', price: 15000, title: '우천 후 작업', desc: '습한 지형 작업 난이도 증가' },
  { id: 'distance', price: 40000, title: '원거리 출장 (50km+)', desc: '장거리 이동 출장비 포함' },
  { id: 'multi', price: 20000, title: '다단 묘지 구조', desc: '계단식 배치로 작업 복잡도 증가' },
  { id: 'fence', price: 25000, title: '울타리/경계석 주변', desc: '정밀 작업 필요 구간 추가' },
  { id: 'debris', price: 20000, title: '폐기물 수거', desc: '작업 후 잔재물 수거 및 처리' },
  { id: 'photo', price: 10000, title: '현장 사진 추가 제공', desc: '50장 이상 고화질 사진 제공' },
  { id: 'consult', price: 15000, title: '전문가 현장 상담', desc: '조경 전문가 방문 상담 서비스' },
  { id: 'urgent', price: 50000, title: '긴급 출동 (48시간 내)', desc: '빠른 일정 처리 긴급 할증' },
  { id: 'soil', price: 20000, title: '토양 정비', desc: '봉분 주변 토양 다짐 및 정비' },
  { id: 'plant', price: 30000, title: '잔디 보식', desc: '훼손된 잔디 보식 서비스' },
  { id: 'record', price: 10000, title: '관리 이력 등록', desc: '디지털 관리 대장 등록 서비스' },
];

const GARDEN_EXTRA_FACTORS = [
  { id: 'fertilizer', price: 40000, title: '전문 시비 작업', desc: '계절별 맞춤 영양제 공급' },
  { id: 'pest', price: 50000, title: '병해충 방제', desc: '친환경 약제를 이용한 병해충 관리' },
  { id: 'irrigation', price: 80000, title: '관수 시스템 점검', desc: '스프링클러 및 호스 시스템 점검' },
  { id: 'mulching', price: 35000, title: '멀칭 작업', desc: '토양 보호 및 수분 유지를 위한 멀칭재 포설' },
  { id: 'edging', price: 30000, title: '경계석 정리', desc: '잔디와 화단 경계 정리 작업' },
  { id: 'aeration', price: 45000, title: '잔디 통기 작업', desc: '토양 통기성 개선 및 뿌리 활성화' },
  { id: 'seeding', price: 60000, title: '잔디 보식/파종', desc: '손상된 잔디 부분 보식 및 새 파종' },
  { id: 'tree_support', price: 40000, title: '수목 지주대 설치', desc: '신규 식재 수목 지지대 설치' },
  { id: 'stone_clean', price: 35000, title: '조경석 세척', desc: '정원 내 조경석 고압 세척' },
  { id: 'path_clean', price: 30000, title: '산책로 청소', desc: '정원 내 보행로 청소 및 정비' },
  { id: 'pond', price: 100000, title: '연못 관리', desc: '연못 청소 및 수질 관리' },
  { id: 'lighting', price: 70000, title: '조경 조명 점검', desc: '정원 조명 시설 점검 및 교체' },
  { id: 'furniture', price: 50000, title: '정원 가구 관리', desc: '벤치, 테이블 등 정원 가구 청소 및 보수' },
  { id: 'seasonal', price: 80000, title: '계절 식재 교체', desc: '계절별 화초 교체 및 식재' },
  { id: 'consultation', price: 60000, title: '정원 디자인 컨설팅', desc: '전문가의 정원 개선 제안 서비스' },
];

interface QuoteSimulatorProps {
  isOpen: boolean;
  onClose: () => void;
  onConsult?: (data: SimulatorQuoteData) => void;
}

export default function QuoteSimulator({ isOpen, onClose, onConsult }: QuoteSimulatorProps) {
  const [serviceType, setServiceType] = useState<'grave' | 'garden'>('grave');
  const [graveCount, setGraveCount] = useState(100000);
  const [areaSize, setAreaSize] = useState(20);
  const [coreChecked, setCoreChecked] = useState<Record<string, boolean>>({});
  const [extraChecked, setExtraChecked] = useState<Record<string, boolean>>({});
  const [showExtra, setShowExtra] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // 서비스 타입 변경 시 초기화
  useEffect(() => {
    setCoreChecked({});
    setExtraChecked({});
    setShowExtra(false);
    if (serviceType === 'grave') {
      setGraveCount(100000);
      setAreaSize(20);
    } else {
      setGraveCount(150000);
      setAreaSize(50);
    }
  }, [serviceType]);

  if (!isOpen) return null;

  const currentOptions = serviceType === 'grave' ? GRAVE_OPTIONS : GARDEN_OPTIONS;
  const currentCoreFactors = serviceType === 'grave' ? GRAVE_CORE_FACTORS : GARDEN_CORE_FACTORS;
  const currentExtraFactors = serviceType === 'grave' ? GRAVE_EXTRA_FACTORS : GARDEN_EXTRA_FACTORS;

  const areaThreshold = serviceType === 'grave' ? 20 : 50;
  const areaUnitPrice = serviceType === 'grave' ? 3000 : 5000;
  const areaExtra = areaSize > areaThreshold ? (areaSize - areaThreshold) * areaUnitPrice : 0;
  
  const coreTotal = currentCoreFactors.reduce((sum, f) => sum + (coreChecked[f.id] ? f.price : 0), 0);
  const extraTotal = currentExtraFactors.reduce((sum, f) => sum + (extraChecked[f.id] ? f.price : 0), 0);
  const total = graveCount + areaExtra + coreTotal + extraTotal;

  const toggleCore = (id: string) => setCoreChecked(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleExtra = (id: string) => setExtraChecked(prev => ({ ...prev, [id]: !prev[id] }));

  const handleReset = () => {
    setCoreChecked({});
    setExtraChecked({});
    if (serviceType === 'grave') {
      setAreaSize(20);
      setGraveCount(100000);
    } else {
      setAreaSize(50);
      setGraveCount(150000);
    }
  };

  const handleConsult = () => {
    if (!onConsult) {
      onClose();
      setTimeout(() => {
        const el = document.getElementById('contact');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return;
    }

    const graveLabel = currentOptions.find(o => o.value === graveCount)?.label ?? '';
    const checkedCoreItems = currentCoreFactors.filter(f => coreChecked[f.id]).map(f => ({ title: f.title, price: f.price }));
    const checkedExtraItems = currentExtraFactors.filter(f => extraChecked[f.id]).map(f => ({ title: f.title, price: f.price }));

    onConsult({
      serviceType,
      graveCount,
      graveLabel,
      areaSize,
      areaExtra,
      coreItems: checkedCoreItems,
      extraItems: checkedExtraItems,
      total,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto relative animate-fade-in">
        {/* 닫기 버튼 */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 hover:bg-slate-100 transition cursor-pointer shadow"
        >
          <i className="ri-close-line text-xl text-slate-600"></i>
        </button>

        {/* Header */}
        <div className="bg-[#166534] p-8 text-white text-center rounded-t-[2rem]">
          <h2 className="text-3xl font-bold mb-2">실시간 견적 시뮬레이터</h2>
          <p className="text-green-100 opacity-90">데이터로 산출하는 정직한 정찰제 견적</p>
        </div>

        <div className="p-8 md:p-10 space-y-10">
          {/* 서비스 유형 선택 탭 */}
          <div>
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <span className="bg-[#166534] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">0</span>
              서비스 유형 선택
            </h3>
            <div className="flex gap-3">
              <button
                onClick={() => setServiceType('grave')}
                className={`flex-1 py-4 px-6 rounded-xl font-bold text-sm transition cursor-pointer ${
                  serviceType === 'grave'
                    ? 'bg-[#166534] text-white shadow-lg'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <i className="ri-plant-line mr-2"></i>
                벌초 서비스
              </button>
              <button
                onClick={() => setServiceType('garden')}
                className={`flex-1 py-4 px-6 rounded-xl font-bold text-sm transition cursor-pointer ${
                  serviceType === 'garden'
                    ? 'bg-[#166534] text-white shadow-lg'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <i className="ri-home-garden-line mr-2"></i>
                전원주택 정원관리
              </button>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Step 1 */}
          <div>
            <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
              <span className="bg-[#166534] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">1</span>
              {serviceType === 'grave' ? '기본 관리 대상 및 면적' : '정원 규모 및 면적'}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  {serviceType === 'grave' ? '관리 대상 (묘지 기수)' : '정원 규모'}
                </label>
                <select
                  value={graveCount}
                  onChange={e => setGraveCount(Number(e.target.value))}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#166534] outline-none transition text-sm cursor-pointer"
                >
                  {currentOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">예상 면적 (평수)</label>
                <input
                  type="number"
                  value={areaSize}
                  min={1}
                  onChange={e => setAreaSize(Number(e.target.value))}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#166534] outline-none transition text-sm"
                  placeholder="평수 입력"
                />
                <p className="text-[10px] text-slate-400 mt-2">
                  * {areaThreshold}평 초과 시 평당 {areaUnitPrice.toLocaleString()}원 추가 비용이 발생합니다.
                </p>
              </div>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Step 2 */}
          <div>
            <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
              <span className="bg-[#166534] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">2</span>
              {serviceType === 'grave' ? '현장 난이도 체크 (핵심 4대 요인)' : '기본 관리 항목 선택'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentCoreFactors.map(f => (
                <label
                  key={f.id}
                  onClick={() => toggleCore(f.id)}
                  className={`flex items-center p-4 rounded-xl cursor-pointer transition border ${
                    coreChecked[f.id]
                      ? 'bg-green-50 border-[#166534]'
                      : 'bg-slate-50 border-transparent hover:bg-slate-100 hover:border-slate-200'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
                    coreChecked[f.id] ? 'bg-[#166534] border-[#166534]' : 'border-slate-300'
                  }`}>
                    {coreChecked[f.id] && <i className="ri-check-line text-white text-xs"></i>}
                  </div>
                  <span className="ml-4">
                    <strong className="block text-sm text-slate-800">{f.title}</strong>
                    <span className="text-[11px] text-slate-500">{f.desc}</span>
                  </span>
                  <span className="ml-auto text-sm font-semibold text-[#166534] whitespace-nowrap">+{f.price.toLocaleString()}원</span>
                </label>
              ))}
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Step 3 - Extra Factors */}
          <div>
            <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
              <span className="bg-[#166534] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">3</span>
              추가 서비스 선택 ({currentExtraFactors.length}개 요인)
            </h3>
            <button
              onClick={() => setShowExtra(v => !v)}
              className="w-full flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:bg-slate-100 transition cursor-pointer mb-4"
            >
              <span className="text-sm font-medium text-slate-700">추가 요인 {showExtra ? '접기' : '펼치기'}</span>
              <i className={`ri-arrow-${showExtra ? 'up' : 'down'}-s-line text-slate-500 text-lg`}></i>
            </button>
            {showExtra && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {currentExtraFactors.map(f => (
                  <label
                    key={f.id}
                    onClick={() => toggleExtra(f.id)}
                    className={`flex items-center p-3 rounded-xl cursor-pointer transition border ${
                      extraChecked[f.id]
                        ? 'bg-green-50 border-[#166534]'
                        : 'bg-slate-50 border-transparent hover:bg-slate-100 hover:border-slate-200'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
                      extraChecked[f.id] ? 'bg-[#166534] border-[#166534]' : 'border-slate-300'
                    }`}>
                      {extraChecked[f.id] && <i className="ri-check-line text-white text-xs"></i>}
                    </div>
                    <span className="ml-3">
                      <strong className="block text-sm text-slate-800">{f.title}</strong>
                      <span className="text-[10px] text-slate-500">{f.desc}</span>
                    </span>
                    <span className="ml-auto text-sm font-semibold text-[#166534] whitespace-nowrap">+{f.price.toLocaleString()}원</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <hr className="border-slate-100" />

          {/* Result */}
          <div className="bg-gradient-to-br from-[#166534] to-[#14532d] rounded-2xl p-8 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="text-green-200 text-sm mb-1">예상 견적 금액</p>
                <div className="text-5xl font-bold tracking-tight">
                  {total.toLocaleString()}
                  <span className="text-2xl font-medium ml-1">원~</span>
                </div>
                <p className="text-green-200 text-xs mt-2">* 현장 실측 후 최종 확정 / 부가세 별도</p>
              </div>
              <div className="space-y-2 text-sm min-w-[200px]">
                <div className="flex justify-between">
                  <span className="text-green-200">기본 관리비</span>
                  <span className="font-medium">{graveCount.toLocaleString()}원</span>
                </div>
                {areaExtra > 0 && (
                  <div className="flex justify-between">
                    <span className="text-green-200">면적 추가비</span>
                    <span className="font-medium">+{areaExtra.toLocaleString()}원</span>
                  </div>
                )}
                {coreTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-green-200">{serviceType === 'grave' ? '난이도 할증' : '기본 관리'}</span>
                    <span className="font-medium">+{coreTotal.toLocaleString()}원</span>
                  </div>
                )}
                {extraTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-green-200">추가 서비스</span>
                    <span className="font-medium">+{extraTotal.toLocaleString()}원</span>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/20 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleConsult}
                className="flex-1 bg-white text-[#166534] text-center py-3 rounded-xl font-bold text-sm hover:bg-green-50 transition cursor-pointer whitespace-nowrap"
              >
                이 견적으로 상담 신청하기
              </button>
              <button
                onClick={handleReset}
                className="flex-1 bg-white/10 text-white text-center py-3 rounded-xl font-medium text-sm hover:bg-white/20 transition cursor-pointer whitespace-nowrap border border-white/20"
              >
                초기화
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}